import { User } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../../hooks/useIsMobile";
import ClubBadge from "../../components/ClubBadge";
import { getClubBadge } from "../../data/clubBadges";
import Modal from "../../components/Modal";
import Paginator from "../../components/admin/shared/Paginator";
import SectionCard from "../../components/admin/shared/SectionCard";
import ConfirmDestructive from "../../components/admin/shared/ConfirmDestructive";
import SortableTh from "../../components/admin/shared/SortableTh";
import { useAdminSort } from "../../hooks/useAdminSort";
import { Club, getClubs, createClub, updateClub, deleteClub } from "../../api/clubs";
import { ApiError } from "../../api/client";
import {
  primaryBtn, secondaryBtn, dangerBtn, smallBtn,
  inputStyle, tableStyle, thStyle, tdStyle, errorStyle,
} from "../../styles";

interface Props {
  user: User;
}

const CLUBS_PAGE_SIZE = 25;

export default function ClubsPage({ user }: Props) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { sort, toggleSort } = useAdminSort({ sortBy: "name", sortDir: "asc" });

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", shortName: "", crest: "" });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  // Edit modal
  const [editClub, setEditClub] = useState<Club | null>(null);
  const [editForm, setEditForm] = useState({ name: "", shortName: "", crest: "" });
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState("");

  // Delete modal
  const [confirmDeleteClub, setConfirmDeleteClub] = useState<Club | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async (s: string, p: number) => {
    setLoading(true);
    setError("");
    try {
      const data = await getClubs(user, { search: s || undefined, page: p, sortBy: sort.sortBy ?? undefined, sortDir: sort.sortDir });
      setClubs(data.clubs);
      setTotal(data.total);
    } catch {
      setError(t("admin.clubs.errors.failedToLoad"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load("", 1); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); load(search, 1); }, 300);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(search, page); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setPage(1); load(search, 1); }, [sort]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async () => {
    if (!createForm.name.trim()) { setCreateError("Name is required"); return; }
    setCreating(true);
    setCreateError("");
    try {
      await createClub(user, {
        name: createForm.name.trim(),
        shortName: createForm.shortName.trim() || undefined,
        crest: createForm.crest.trim() || undefined,
      });
      setShowCreate(false);
      setCreateForm({ name: "", shortName: "", crest: "" });
      load(search, page);
    } catch {
      setCreateError(t("admin.clubs.errors.failedToCreate"));
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (club: Club) => {
    setEditClub(club);
    setEditForm({ name: club.name, shortName: club.shortName ?? "", crest: club.crest ?? "" });
    setEditError("");
  };

  const handleEdit = async () => {
    if (!editClub || !editForm.name.trim()) { setEditError("Name is required"); return; }
    setSaving(true);
    setEditError("");
    try {
      await updateClub(user, editClub.id, {
        name: editForm.name.trim(),
        shortName: editForm.shortName.trim() || null,
        crest: editForm.crest.trim() || null,
      });
      setEditClub(null);
      load(search, page);
    } catch {
      setEditError(t("admin.clubs.errors.failedToEdit"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteClub) return;
    setDeleting(true);
    try {
      await deleteClub(user, confirmDeleteClub.id);
      setConfirmDeleteClub(null);
      load(search, page);
    } catch (err) {
      setError(err instanceof ApiError && err.status === 409 ? t("admin.clubs.errors.inUse") : t("admin.clubs.errors.failedToDelete"));
      setConfirmDeleteClub(null);
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.ceil(total / CLUBS_PAGE_SIZE);

  return (
    <div>
      <SectionCard
        title={t("admin.clubs.title")}
        actions={
          <>
            <input
              style={{ ...inputStyle, width: "200px" }}
              placeholder="Search clubs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button style={primaryBtn} onClick={() => setShowCreate(true)}>
              {t("admin.clubs.create")}
            </button>
          </>
        }
      >
        {error && <p style={{ ...errorStyle, margin: "0.75rem 1rem 0" }}>{error}</p>}
        {loading ? <p style={{ padding: "1rem", color: "var(--color-text-muted)" }}>{t("common.loading")}</p> : (
          <>
            {clubs.length === 0 && (
              <p style={{ padding: "1rem", color: "var(--color-text-muted)" }}>{t("admin.clubs.noClubs")}</p>
            )}
            {clubs.length > 0 && (
              <div style={isMobile ? { overflowX: "auto" } : undefined}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <SortableTh label={t("admin.clubs.columns.name")} sortKey="name" activeSortBy={sort.sortBy} sortDir={sort.sortDir} onSort={toggleSort} />
                      <SortableTh label={t("admin.clubs.columns.shortName")} sortKey="shortName" activeSortBy={sort.sortBy} sortDir={sort.sortDir} onSort={toggleSort} />
                      <th style={thStyle}>{t("admin.clubs.columns.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clubs.map((club) => (
                      <tr key={club.id}>
                        <td style={tdStyle}>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                            <ClubBadge config={getClubBadge(club.name)} size={24} title={club.name} />
                            {club.name}
                          </div>
                        </td>
                        <td style={tdStyle}>{club.shortName ?? "—"}</td>
                        <td style={tdStyle}>
                          <div style={{ display: "flex", gap: "0.4rem" }}>
                            <button style={smallBtn} onClick={() => openEdit(club)}>{t("common.edit")}</button>
                            <button style={dangerBtn} onClick={() => setConfirmDeleteClub(club)}>{t("common.delete")}</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Paginator page={page} totalPages={totalPages} onPageChange={setPage} />
          </>
        )}
      </SectionCard>

      {/* Create modal */}
      {showCreate && (
        <Modal onClose={() => { setShowCreate(false); setCreateError(""); setCreateForm({ name: "", shortName: "", crest: "" }); }} blur={false}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--color-surface)", borderRadius: "20px", border: "1px solid var(--color-border)", padding: "1.5rem", maxWidth: "400px", width: "90%" }}>
            <h3 style={{ marginTop: 0 }}>{t("admin.clubs.createModal.title")}</h3>
            {createError && <p style={errorStyle}>{createError}</p>}
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem" }}>
              <ClubBadge config={getClubBadge(createForm.name)} size={40} title={createForm.name} />
              <div style={{ flex: 1 }}>
                <label style={{ display: "block", fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.25rem" }}>{t("admin.clubs.createModal.nameLabel")}</label>
                <input type="text" style={inputStyle} value={createForm.name} onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
            </div>
            <div style={{ marginBottom: "0.75rem" }}>
              <label style={{ display: "block", fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.25rem" }}>{t("admin.clubs.createModal.shortNameLabel")}</label>
              <input type="text" style={inputStyle} placeholder="e.g. Man City, Spurs" value={createForm.shortName} onChange={(e) => setCreateForm((f) => ({ ...f, shortName: e.target.value }))} />
            </div>
            <div style={{ marginBottom: "0.75rem" }}>
              <label style={{ display: "block", fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.25rem" }}>{t("admin.clubs.createModal.crestLabel")}</label>
              <input type="text" style={inputStyle} placeholder="https://…" value={createForm.crest} onChange={(e) => setCreateForm((f) => ({ ...f, crest: e.target.value }))} />
            </div>
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", marginTop: "1rem" }}>
              <button style={secondaryBtn} onClick={() => { setShowCreate(false); setCreateError(""); setCreateForm({ name: "", shortName: "", crest: "" }); }}>
                {t("common.cancel")}
              </button>
              <button style={primaryBtn} onClick={handleCreate} disabled={creating}>
                {creating ? t("common.saving") : t("admin.clubs.createModal.submit")}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit modal */}
      {editClub && (
        <Modal onClose={() => setEditClub(null)} blur={false}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--color-surface)", borderRadius: "20px", border: "1px solid var(--color-border)", padding: "1.5rem", maxWidth: "400px", width: "90%" }}>
            <h3 style={{ marginTop: 0 }}>{t("admin.clubs.editModal.title")}</h3>
            {editError && <p style={errorStyle}>{editError}</p>}
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem" }}>
              <ClubBadge config={getClubBadge(editForm.name ?? editClub?.name ?? "")} size={40} title={editForm.name ?? ""} />
              <div style={{ flex: 1 }}>
                <label style={{ display: "block", fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.25rem" }}>{t("admin.clubs.editModal.nameLabel")}</label>
                <input type="text" style={inputStyle} value={editForm.name ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
            </div>
            <div style={{ marginBottom: "0.75rem" }}>
              <label style={{ display: "block", fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.25rem" }}>{t("admin.clubs.editModal.shortNameLabel")}</label>
              <input type="text" style={inputStyle} placeholder="e.g. Man City, Spurs" value={editForm.shortName ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, shortName: e.target.value }))} />
            </div>
            <div style={{ marginBottom: "0.75rem" }}>
              <label style={{ display: "block", fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "0.25rem" }}>{t("admin.clubs.editModal.crestLabel")}</label>
              <input type="text" style={inputStyle} placeholder="https://…" value={editForm.crest ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, crest: e.target.value }))} />
            </div>
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", marginTop: "1rem" }}>
              <button style={secondaryBtn} onClick={() => setEditClub(null)}>{t("common.cancel")}</button>
              <button style={primaryBtn} onClick={handleEdit} disabled={saving}>
                {saving ? t("common.saving") : t("admin.clubs.editModal.submit")}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete confirm */}
      {confirmDeleteClub && (
        <ConfirmDestructive
          title={t("admin.clubs.deleteModal.title")}
          message={t("admin.clubs.deleteModal.message", { name: confirmDeleteClub.name })}
          confirmLabel={t("admin.clubs.deleteModal.confirm")}
          confirming={deleting}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDeleteClub(null)}
        />
      )}
    </div>
  );
}
